import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";

const normalizeProductId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const normalizeQuantity = (value, fallback = 1) => {
  const quantity = Number(value);
  return Number.isFinite(quantity) && quantity > 0
    ? Math.max(1, Math.floor(quantity))
    : fallback;
};

const normalizeOption = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeCartItems = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((items, item) => {
    if (!item || typeof item !== "object") return items;

    const id = normalizeProductId(item.id);
    if (id === null) return items;

    const normalizedItem = {
      id,
      size: normalizeOption(item.size),
      color: normalizeOption(item.color),
      quantity: normalizeQuantity(item.quantity),
    };
    const matchingItem = items.find(
      (entry) =>
        entry.id === normalizedItem.id &&
        entry.size === normalizedItem.size &&
        entry.color === normalizedItem.color
    );

    if (matchingItem) {
      matchingItem.quantity += normalizedItem.quantity;
    } else {
      items.push(normalizedItem);
    }

    return items;
  }, []);
};

const normalizeWishlistItems = (value) => {
  if (!Array.isArray(value)) return [];

  return [...new Set(value.map(normalizeProductId).filter((id) => id !== null))];
};

const readStoredList = (key, normalize) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return [];

    const storedValue = window.localStorage.getItem(key);
    return storedValue ? normalize(JSON.parse(storedValue)) : [];
  } catch {
    return [];
  }
};

const writeStoredList = (key, value) => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Browsers may block storage in private or restricted contexts.
  }
};

export const CartContext = createContext({
  cartItems: [],
  wishlistItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  toggleWishlist: () => {},
  isWishlisted: () => false,
});

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredList(CART_STORAGE_KEY, normalizeCartItems)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredList(WISHLIST_STORAGE_KEY, normalizeWishlistItems)
  );

  useEffect(() => {
    writeStoredList(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    writeStoredList(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  const addToCart = useCallback((productId, quantity, size, color) => {
    const id = normalizeProductId(productId);
    if (id === null) return;

    const normalizedQuantity = normalizeQuantity(quantity);
    const normalizedSize = normalizeOption(size);
    const normalizedColor = normalizeOption(color);

    setCartItems((previousItems) => {
      const matchingItem = previousItems.find(
        (item) =>
          item.id === id &&
          item.size === normalizedSize &&
          item.color === normalizedColor
      );

      if (!matchingItem) {
        return [
          ...previousItems,
          {
            id,
            size: normalizedSize,
            color: normalizedColor,
            quantity: normalizedQuantity,
          },
        ];
      }

      return previousItems.map((item) =>
        item.id === id &&
        item.size === normalizedSize &&
        item.color === normalizedColor
          ? { ...item, quantity: item.quantity + normalizedQuantity }
          : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, size, color) => {
    const id = normalizeProductId(productId);
    if (id === null) return;

    const normalizedSize = normalizeOption(size);
    const normalizedColor = normalizeOption(color);
    setCartItems((previousItems) =>
      previousItems.filter(
        (item) =>
          !(
            item.id === id &&
            item.size === normalizedSize &&
            item.color === normalizedColor
          )
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    const id = normalizeProductId(productId);
    if (id === null) return;

    const normalizedSize = normalizeOption(size);
    const normalizedColor = normalizeOption(color);
    const normalizedQuantity = normalizeQuantity(quantity);

    setCartItems((previousItems) =>
      previousItems.map((item) =>
        item.id === id &&
        item.size === normalizedSize &&
        item.color === normalizedColor
          ? { ...item, quantity: normalizedQuantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const addToWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);
    if (id === null) return;

    setWishlistItems((previousItems) =>
      previousItems.includes(id) ? previousItems : [...previousItems, id]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);
    if (id === null) return;

    setWishlistItems((previousItems) =>
      previousItems.filter((itemId) => itemId !== id)
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);
    if (id === null) return;

    setWishlistItems((previousItems) =>
      previousItems.includes(id)
        ? previousItems.filter((itemId) => itemId !== id)
        : [...previousItems, id]
    );
  }, []);

  const isWishlisted = useCallback(
    (productId) => {
      const id = normalizeProductId(productId);
      return id !== null && wishlistItems.includes(id);
    },
    [wishlistItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted,
    }),
    [
      cartItems,
      wishlistItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
